import React, { useCallback, useEffect } from 'react';

import { useDashboardData } from './hooks/useDashboardData';
import { useNavbarSlot } from '@/contexts/NavbarSlot.context';
import { SingleDatePicker } from '../TeamDashboard/components/SingleDatePicker.component';
import { Dropdown } from '@/components/Dropdown';
import { OverallProductivity } from './components/OverallProductivity.component';
import { KeyHighlights } from './components/KeyHighlights.component';
import { ProductivityOverTime } from './components/ProductivityOverTime.component';
import { ActivityBreakdown } from './components/ActivityBreakdown.component';
import { ActivityHeatmap } from './components/ActivityHeatmap.component';
import { TopApplications } from './components/TopApplications.component';
import { FocusVsDistraction } from './components/FocusVsDistraction.component';
import { AIInsights } from './components/AIInsights.component';
import { RecentAlerts } from './components/RecentAlerts.component';
import { AttendanceSummary } from './components/AttendanceSummary.component';
import { AI_INSIGHTS_DATA } from './data/dashboardOverviewNew.data';
import styles from './DashboardOverviewNew.module.scss';

export const DashboardOverviewNewComponent = () => {
  const {
    dateRange,
    setDateRange,
    membersOptions,
    selectedMember,
    setSelectedMember,
    isLoadingMembers,
    isFetchingStats,
    isFetchingAlerts,
    overallProductivityData,
    keyHighlightsData,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    recentAlertsData,
    productivityOverTimeData,
    chartTicks,
    activityHeatmapData,
    attendanceSummaryData,
  } = useDashboardData();

  const setNavbarSlot = useNavbarSlot();

  const handleDateChange = useCallback(
    (dateStr) => setDateRange({ start: dateStr, end: dateStr }),
    [setDateRange]
  );

  useEffect(() => {
    setNavbarSlot(
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '220px' }}>
          <Dropdown
            options={membersOptions}
            value={selectedMember}
            onChange={setSelectedMember}
            isLoading={isLoadingMembers}
            isDisabled={isLoadingMembers}
            placeholder="Select member..."
            selectStyles={{
              container: {
                border: '1px solid #E0E4EA',
                borderRadius: '10px',
                boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
              },
              control: { height: '48px', borderRadius: '10px' },
              valueContainer: { height: '48px' },
            }}
          />
        </div>
        <SingleDatePicker
          date={dateRange.start}
          onDateChange={handleDateChange}
        />
      </div>
    );
    return () => setNavbarSlot(null);
  }, [
    dateRange.start,
    membersOptions,
    selectedMember,
    isLoadingMembers,
    handleDateChange,
    setSelectedMember,
    setNavbarSlot,
  ]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.row}>
        <OverallProductivity
          data={overallProductivityData}
          isLoading={isFetchingStats}
        />
        <KeyHighlights data={keyHighlightsData} isLoading={isFetchingStats} />
      </div>

      <div className={styles.row2}>
        <ProductivityOverTime
          data={productivityOverTimeData}
          isLoading={isFetchingStats}
          chartTicks={chartTicks}
        />
        <ActivityBreakdown
          data={activityBreakdownData}
          isLoading={isFetchingStats}
        />
        <ActivityHeatmap
          data={activityHeatmapData}
          isLoading={isFetchingStats}
        />
      </div>

      <div className={styles.row3}>
        <TopApplications
          data={topApplicationsData}
          isLoading={isFetchingStats}
        />
        <FocusVsDistraction
          data={focusVsDistractionData}
          isLoading={isFetchingStats}
        />
        <AIInsights data={AI_INSIGHTS_DATA} />
        <RecentAlerts data={recentAlertsData} isLoading={isFetchingAlerts} />
      </div>

      <div className={styles.row4}>
        <AttendanceSummary
          data={attendanceSummaryData}
          isLoading={isFetchingStats}
        />
      </div>
    </div>
  );
};
